import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';

import { useKeyHint } from '../../lib/shortcuts/index.js';
import { useDocHistory } from '../../lib/use-doc-history.js';
import { useWorkspace } from '../../state/workspace.js';
import { IconButton, isTyping, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import {
  MASTER,
  MAX_DB,
  MIN_DB,
  addBus,
  addSnapshot,
  busNames,
  descendants,
  findBus,
  formatDb,
  parentOf,
  parseMixer,
  removeBus,
  removeSnapshot,
  renameBus,
  renameSnapshot,
  reparentBus,
  serializeMixer,
  setBusFlag,
  setBusVolumeDb,
  setOverride,
  snapshotOf,
  treeRows,
  volumeDbOf,
  type MixerDoc,
} from './mixer-doc.js';
import './mixer.css';

/** Edits closer together than this with the same key form one undo step (slider drags). */
const COALESCE_MS = 700;

/**
 * Visual editor for one `*.mixer.json`: the bus tree (add / remove /
 * reparent by drag or the Parent field, volume in dB, mute, solo), snapshots
 * (per-bus volume / mute overrides) and the start snapshot. Edits the open
 * buffer, autosaves, Ctrl+Z / Ctrl+Shift+Z undo / redo.
 */
export function MixerEditor({ path, onShowText }: { path: string; onShowText: () => void }) {
  const file = useWorkspace((s) => s.files[path]);
  const setContent = useWorkspace((s) => s.setContent);
  const saveFile = useWorkspace((s) => s.saveFile);
  const content = file?.content ?? '';
  const parsed = useMemo(() => parseMixer(content), [content]);
  const doc = parsed.doc;

  const [selected, setSelected] = useState<string>(MASTER);
  /** Snapshot being edited ('' = the asset's own bus values). */
  const [snapshot, setSnapshot] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commit = useCallback(
    (text: string) => {
      setContent(path, text);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void saveFile(path), 500);
    },
    [path, setContent, saveFile],
  );

  // Shared undo service: Ctrl+Z / Ctrl+Shift+Z, the Edit menu and the Undo History panel.
  const history = useDocHistory<string>({
    id: `mixer:${path}`,
    label: `Mixer · ${path.split('/').pop()}`,
    view: 'code',
    read: () => useWorkspace.getState().files[path]?.content ?? '',
    apply: commit,
    limit: 200,
    coalesceMs: COALESCE_MS,
  });
  const undoKey = useKeyHint('edit.undo');
  const redoKey = useKeyHint('edit.redo');

  /** Applies `fn` to a copy of the document; `key` merges rapid edits (sliders) into one undo step. */
  const edit = useCallback(
    (fn: (d: MixerDoc) => boolean | void, key?: string, label?: string) => {
      if (!doc) return;
      const next = structuredClone(doc);
      if (fn(next) === false) return;
      const text = serializeMixer(next);
      if (text === content) return;
      const name = label ?? (key?.startsWith('vol:') ? `${key.slice(4)} volume` : key?.startsWith('ovr:') ? 'Snapshot volume' : 'Edit mixer');
      history.record(content, name, key);
      commit(text);
    },
    [doc, content, commit, history],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected !== MASTER) {
        e.preventDefault();
        edit((d) => removeBus(d, selected), undefined, 'Remove bus');
        setSelected(MASTER);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [edit, selected]);

  // Save pending edits when the editor closes.
  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        void saveFile(path);
      }
    },
    [path, saveFile],
  );

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3000);
    return () => clearTimeout(t);
  }, [notice]);

  if (!file) return null;
  if (!doc) {
    return (
      <div className="mixed mixed-broken">
        <Icon name="warning" size={22} />
        <p>This mixer is not valid JSON: {parsed.error}</p>
        <button type="button" className="mixed-chip" onClick={onShowText}>
          Fix it as text
        </button>
      </div>
    );
  }

  const names = busNames(doc);
  const bus = names.includes(selected) ? selected : MASTER;
  const snapshots = doc.snapshots ?? [];
  const snap = snapshot ? snapshotOf(doc, snapshot) : undefined;
  const editingSnapshot = snap?.name ?? '';
  const rows = treeRows(doc);
  const anySolo = doc.buses.some((b) => b.solo);

  function reparent(name: string, parent: string) {
    let ok = true;
    edit((d) => {
      ok = reparentBus(d, name, parent);
      return ok;
    });
    if (!ok) setNotice(`${name} cannot go under ${parent}: that would make a loop`);
  }

  function onDrop(e: DragEvent, target: string) {
    e.preventDefault();
    const from = dragging ?? e.dataTransfer.getData('text/x-prism-bus');
    setDragging(null);
    setDropTarget(null);
    if (from && from !== target && parentOf(doc!, from) !== target) reparent(from, target);
  }

  return (
    <div className="mixed">
      <div className="mixed-toolbar">
        <Icon name="audio" size={15} />
        <strong className="mixed-title">Mixer</strong>
        <IconButton icon="undo" title={history.undoLabel ? `Undo ${history.undoLabel}` : 'Undo'} shortcut={undoKey} disabled={!history.canUndo} onClick={history.undo} />
        <IconButton icon="redo" title={history.redoLabel ? `Redo ${history.redoLabel}` : 'Redo'} shortcut={redoKey} disabled={!history.canRedo} onClick={history.redo} />
        <span className="mixed-sep" />
        <span className="muted">Editing</span>
        <SelectField
          value={editingSnapshot}
          options={[{ value: '', label: 'Bus values (asset)' }, ...snapshots.map((s) => ({ value: s.name, label: `Snapshot: ${s.name}` }))]}
          onChange={setSnapshot}
        />
        <span className="mixed-spacer" />
        {notice && <span className="mixed-notice">{notice}</span>}
        <button type="button" className="mixed-chip" onClick={onShowText}>
          Edit as text
        </button>
      </div>

      <div className="mixed-body">
        <div className="mixed-tree" role="tree" aria-label="Buses">
          {editingSnapshot && (
            <div className="mixed-banner">
              Editing snapshot <strong>{editingSnapshot}</strong>: moving a slider or toggling mute overrides that bus; buses
              without an override keep their asset values.
            </div>
          )}
          {rows.map(({ name, depth }) => {
            const b = findBus(doc, name);
            const override = snap?.buses[name];
            const hasVolume = override?.volumeDb !== undefined || override?.volume !== undefined;
            const db = editingSnapshot && hasVolume ? volumeDbOf(override) : volumeDbOf(b);
            const muted = editingSnapshot && override?.mute !== undefined ? override.mute : !!b?.mute;
            const audible = !anySolo || [...descendants(doc, name)].some((n) => findBus(doc, n)?.solo) || soloedAncestor(doc, name);
            const overridden = !!override && Object.keys(override).length > 0;
            return (
              <div
                key={name}
                role="treeitem"
                aria-selected={name === bus}
                className={`mixed-row${name === bus ? ' is-selected' : ''}${dropTarget === name ? ' is-drop' : ''}${!audible || muted ? ' is-silent' : ''}`}
                style={{ paddingLeft: 8 + depth * 18 }}
                draggable={name !== MASTER}
                onClick={() => setSelected(name)}
                onDragStart={(e) => {
                  setDragging(name);
                  e.dataTransfer.setData('text/x-prism-bus', name);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => {
                  setDragging(null);
                  setDropTarget(null);
                }}
                onDragOver={(e) => {
                  if (dragging && !descendants(doc, dragging).has(name)) {
                    e.preventDefault();
                    setDropTarget(name);
                  }
                }}
                onDragLeave={() => setDropTarget((t) => (t === name ? null : t))}
                onDrop={(e) => onDrop(e, name)}
                title={name === MASTER ? 'Master: every bus ends here' : 'Drag onto another bus to move it there'}
              >
                <span className="mixed-name">
                  {depth > 0 && <span className="mixed-branch" aria-hidden="true" />}
                  {name}
                  {editingSnapshot && overridden && <span className="mixed-ovr" title="This snapshot overrides this bus">S</span>}
                </span>
                <button
                  type="button"
                  className={`mixed-flag mute${muted ? ' is-on' : ''}${editingSnapshot && override?.mute === undefined ? ' is-inherited' : ''}`}
                  title={editingSnapshot ? 'Mute in this snapshot (click: on / off / back to asset value)' : 'Mute'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editingSnapshot) {
                      const cur = override?.mute;
                      const next = cur === undefined ? !b?.mute : cur === !b?.mute ? !cur : null;
                      edit((d) => setOverride(d, editingSnapshot, name, { mute: next }));
                    } else edit((d) => setBusFlag(d, name, 'mute', !b?.mute));
                  }}
                >
                  M
                </button>
                <button
                  type="button"
                  className={`mixed-flag solo${b?.solo ? ' is-on' : ''}`}
                  title={editingSnapshot ? 'Solo is a bus setting (snapshots do not change it)' : 'Solo: only soloed buses (with their parents and children) are heard'}
                  disabled={!!editingSnapshot}
                  onClick={(e) => {
                    e.stopPropagation();
                    edit((d) => setBusFlag(d, name, 'solo', !b?.solo));
                  }}
                >
                  S
                </button>
                <input
                  className={`mixed-slider${editingSnapshot && !hasVolume ? ' is-inherited' : ''}`}
                  type="range"
                  min={MIN_DB}
                  max={MAX_DB}
                  step={0.5}
                  value={db}
                  aria-label={`${name} volume`}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (editingSnapshot) edit((d) => setOverride(d, editingSnapshot, name, { volumeDb: v }), `ovr:${editingSnapshot}:${name}`);
                    else edit((d) => setBusVolumeDb(d, name, v), `vol:${name}`);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (editingSnapshot) edit((d) => setOverride(d, editingSnapshot, name, { volumeDb: null }));
                    else edit((d) => setBusVolumeDb(d, name, 0));
                  }}
                  title="Volume (double-click: 0 dB, or in a snapshot: back to the asset value)"
                />
                <span className="mixed-db">{formatDb(db)}</span>
              </div>
            );
          })}
          <div className="mixed-tree-actions">
            <button
              type="button"
              className="mixed-chip"
              onClick={() => {
                let added = '';
                edit((d) => {
                  added = addBus(d, bus);
                });
                if (added) setSelected(added);
              }}
            >
              + Bus under {bus}
            </button>
          </div>
        </div>

        <aside className="mixed-side">
          <BusInspector doc={doc} bus={bus} edit={edit} onRenamed={setSelected} onRemoved={() => setSelected(MASTER)} snapshot={editingSnapshot} />
          <section className="mixed-section">
            <header className="mixed-section-head">
              <span>Snapshots</span>
              <button
                type="button"
                className="mixed-chip"
                title="Add a snapshot and edit it"
                onClick={() => {
                  let added = '';
                  edit((d) => {
                    added = addSnapshot(d);
                  });
                  if (added) setSnapshot(added);
                }}
              >
                + Snapshot
              </button>
            </header>
            {snapshots.length === 0 && (
              <p className="muted mixed-help">
                Snapshots store bus volumes / mutes (e.g. a "Paused" mix). Scripts blend to one with{' '}
                <code>audio_snapshot("Paused", 0.3)</code>.
              </p>
            )}
            {snapshots.map((s) => (
              <div key={s.name} className={`mixed-snap${s.name === editingSnapshot ? ' is-selected' : ''}`}>
                <TextField
                  value={s.name}
                  onChange={(v) => {
                    let ok = false;
                    edit((d) => {
                      ok = renameSnapshot(d, s.name, v);
                      return ok;
                    });
                    if (ok && editingSnapshot === s.name) setSnapshot(v.trim());
                    else if (!ok && v.trim() !== s.name) setNotice(`"${v}" is empty or already used`);
                  }}
                />
                <span className="muted mixed-count" title="Buses this snapshot overrides">
                  {Object.keys(s.buses).length}
                </span>
                <button type="button" className={`mixed-chip${s.name === editingSnapshot ? ' is-on' : ''}`} onClick={() => setSnapshot(s.name === editingSnapshot ? '' : s.name)}>
                  {s.name === editingSnapshot ? 'Done' : 'Edit'}
                </button>
                <IconButton
                  icon="trash"
                  title={`Delete snapshot ${s.name}`}
                  onClick={() => {
                    edit((d) => removeSnapshot(d, s.name));
                    if (editingSnapshot === s.name) setSnapshot('');
                  }}
                />
              </div>
            ))}
            <PropRow label="Start snapshot" hint="Applied when play starts (empty = the bus values)">
              <SelectField
                value={doc.startSnapshot ?? ''}
                options={[{ value: '', label: 'None' }, ...snapshots.map((s) => s.name)]}
                onChange={(v) =>
                  edit((d) => {
                    if (v) d.startSnapshot = v;
                    else delete d.startSnapshot;
                  })
                }
              />
            </PropRow>
          </section>
        </aside>
      </div>
    </div>
  );
}

/** A soloed parent makes its children audible too. */
function soloedAncestor(doc: MixerDoc, name: string): boolean {
  const seen = new Set<string>();
  let p = parentOf(doc, name);
  while (p && !seen.has(p)) {
    if (findBus(doc, p)?.solo) return true;
    seen.add(p);
    p = parentOf(doc, p);
  }
  return false;
}

function BusInspector({
  doc,
  bus,
  edit,
  onRenamed,
  onRemoved,
  snapshot,
}: {
  doc: MixerDoc;
  bus: string;
  edit: (fn: (d: MixerDoc) => boolean | void, key?: string) => void;
  onRenamed: (name: string) => void;
  onRemoved: () => void;
  snapshot: string;
}) {
  const b = findBus(doc, bus);
  const isMaster = bus === MASTER;
  const blocked = descendants(doc, bus);
  const parents = busNames(doc).filter((n) => !blocked.has(n));
  const override = snapshot ? snapshotOf(doc, snapshot)?.buses[bus] : undefined;
  const hasVolume = override?.volumeDb !== undefined || override?.volume !== undefined;
  return (
    <section className="mixed-section">
      <header className="mixed-section-head">
        <span>Bus</span>
        {!isMaster && (
          <IconButton
            icon="trash"
            title={`Remove ${bus} (its buses move to its parent)`}
            onClick={() => {
              edit((d) => removeBus(d, bus));
              onRemoved();
            }}
          />
        )}
      </header>
      <PropRow label="Name" hint={isMaster ? 'Master always exists' : 'Renaming updates parents and snapshots; update audioSource outputs and scripts too'}>
        {isMaster ? (
          <span className="mixed-static">Master</span>
        ) : (
          <TextField
            value={bus}
            onChange={(v) => {
              let ok = false;
              edit((d) => {
                ok = renameBus(d, bus, v);
                return ok;
              });
              if (ok) onRenamed(v.trim());
            }}
          />
        )}
      </PropRow>
      {!isMaster && (
        <PropRow label="Parent" hint="The bus this one feeds into (its volume multiplies)">
          <SelectField
            value={parentOf(doc, bus) ?? MASTER}
            options={parents}
            onChange={(v) =>
              edit((d) => {
                return reparentBus(d, bus, v);
              })
            }
          />
        </PropRow>
      )}
      <PropRow label="Volume (dB)" hint="0 = unchanged, -80 = silent, up to +20">
        <NumberField value={Math.round(volumeDbOf(b) * 10) / 10} step={0.5} min={MIN_DB} max={MAX_DB} onChange={(v) => edit((d) => setBusVolumeDb(d, bus, v), `vol:${bus}`)} />
      </PropRow>
      <PropRow label="Mute">
        <Toggle checked={!!b?.mute} onChange={(on) => edit((d) => setBusFlag(d, bus, 'mute', on))} />
      </PropRow>
      <PropRow label="Solo" hint="When any bus is soloed only soloed buses, their parents and children are heard">
        <Toggle checked={!!b?.solo} onChange={(on) => edit((d) => setBusFlag(d, bus, 'solo', on))} />
      </PropRow>
      {snapshot && (
        <>
          <div className="mixed-subhead">In snapshot “{snapshot}”</div>
          <PropRow label="Override volume">
            <Toggle
              checked={hasVolume}
              onChange={(on) => edit((d) => setOverride(d, snapshot, bus, { volumeDb: on ? volumeDbOf(b) : null }))}
            />
          </PropRow>
          {hasVolume && (
            <PropRow label="Volume (dB)">
              <NumberField
                value={Math.round(volumeDbOf(override) * 10) / 10}
                step={0.5}
                min={MIN_DB}
                max={MAX_DB}
                onChange={(v) => edit((d) => setOverride(d, snapshot, bus, { volumeDb: v }), `ovr:${snapshot}:${bus}`)}
              />
            </PropRow>
          )}
          <PropRow label="Override mute">
            <Toggle checked={override?.mute !== undefined} onChange={(on) => edit((d) => setOverride(d, snapshot, bus, { mute: on ? !b?.mute : null }))} />
          </PropRow>
          {override?.mute !== undefined && (
            <PropRow label="Muted">
              <Toggle checked={override.mute} onChange={(on) => edit((d) => setOverride(d, snapshot, bus, { mute: on }))} />
            </PropRow>
          )}
        </>
      )}
    </section>
  );
}
